import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import HeaderLoggedIn from "../../components/HeaderLoggedIn";
import Footer from "../../components/Footer";
import { useSelector } from "react-redux";
import { selectCurrentToken } from "../../features/auth/authSlice";
import { UnreadMessagesProvider } from "../../context/UnreadMessagesContext";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link as MuiLink
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: "#f8f9fa",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1.5),
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  margin: theme.spacing(2, 0),
}));

const SectionBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ContactBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  marginTop: theme.spacing(3),
}));

const TermsAndConditions = () => {
  const { t } = useTranslation(["TermsAndConditions"]);
  const token = useSelector(selectCurrentToken);
  const isLoggedIn = !!token;

  useEffect(() => {
    // Set page title
    document.title = "Bidd - " + t("pageTitle");
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("metaDescription"));
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = t("metaDescription");
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, [t]);

  return (
    <>
      {isLoggedIn ? (
        <UnreadMessagesProvider>
          <HeaderLoggedIn />
        </UnreadMessagesProvider>
      ) : (
        <Header />
      )}
      <StyledContainer maxWidth="md">
        <StyledPaper>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              fontWeight: 700,
              color: "#1a1a1a",
              borderBottom: "3px solid",
              borderColor: "primary.main",
              paddingBottom: 2,
              marginBottom: 3
            }}
          >
            {t("title")}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            align="center"
            sx={{ fontStyle: "italic", marginBottom: 4 }}
          >
            {t("lastUpdated")}: {t("lastUpdatedDate")}
          </Typography>
          
          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("introduction.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("introduction.content")}
            </Typography>
          </SectionBox>

          <Divider sx={{ my: 3 }} />

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("personalData.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("personalData.definition")}
            </Typography>
          </SectionBox>

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("dataCollection.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("dataCollection.overview")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("dataCollection.dataTypes")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("dataCollection.purpose")}
            </Typography>
          </SectionBox>

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("dataAccess.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("dataAccess.content")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("dataAccess.processors")}
            </Typography>
          </SectionBox>

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("cookies.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("cookies.content")}
            </Typography>
          </SectionBox>

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("security.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("security.content")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("security.measures")}
            </Typography>
          </SectionBox>

          <SectionBox>
            <Typography variant="h5" component="h2" gutterBottom color="primary">
              {t("rights.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("rights.intro")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("rights.access")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("rights.portability")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("rights.correction")}
            </Typography>
          </SectionBox>

          <ContactBox>
            <Typography variant="h6" component="h2" gutterBottom color="primary">
              {t("contact.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("contact.content")}
            </Typography>
            <Typography variant="body1">
              {t("contact.email")}: {" "}
              <MuiLink 
                href="mailto:bidd@bidd.is" 
                underline="hover"
                sx={{ 
                  color: '#007bff !important',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#0056b3 !important',
                    textDecoration: 'underline'
                  },
                  '&:visited': {
                    color: '#007bff !important'
                  }
                }}
              >
                bidd@bidd.is
              </MuiLink>
            </Typography>
          </ContactBox>
        </StyledPaper>
      </StyledContainer>
      <Footer />
    </>
  );
};

export default TermsAndConditions;
